/**
 * Motorrijtuigenbelasting (MRB / wegenbelasting) for a personenauto.
 *
 * The amount is a rijksdeel from the Wet MRB 1994 plus provinciale opcenten.
 * The rijksdeel table is statutory (art. 23); the base the opcenten are levied
 * on is *not* published — art. 222 lid 3 Provinciewet points at the tariff as
 * it stood on 1 April 1995, indexed yearly by ministerial regulation, and the
 * Belastingdienst only exposes it through an interactive rekenhulp.
 *
 * So the opcenten base here is **calibrated against real 2026 assessments**:
 * 24 cars × 6 months of booked Belastingdienst charges (province Utrecht).
 * Every car landed within € 2,10 per quarter, which is inside the ± € 1,50 of
 * noise that whole-euro monthly rounding already introduces. Two things that
 * calibration settled, both of which a literal reading of the law got wrong:
 *   - the mass is rounded to the NEAREST 100 kg, not down (proven by a Polo at
 *     1045 kg paying a full class less than one at 1055 kg);
 *   - the electric percentage applies to the opcenten as well, not just to the
 *     rijksdeel.
 *
 * Treat the result as a well-founded estimate, not as an assessment. Rates are
 * indexed every January and the electric percentage steps up in 2029, so the
 * constants below need a yearly check against a real charge.
 */
/**
 * Provinciale opcenten 2026, in percent (source: CBS 80889ned). Reference data
 * — knowing the percentage is not enough to price a car, see below.
 */
export const OPCENTEN_2026 = {
    Groningen: 95.7,
    Fryslân: 92.1,
    Drenthe: 92.0,
    Overijssel: 82.2,
    Flevoland: 84.7,
    Gelderland: 98.3,
    Utrecht: 86.4,
    'Noord-Holland': 82.1,
    'Zuid-Holland': 104.4,
    Zeeland: 84.4,
    'Noord-Brabant': 87.0,
    Limburg: 88.5,
};
/**
 * Provinces we can actually price. The opcenten base was fitted *given*
 * Utrecht's 86,4%, so base and percentage are entangled: Utrecht comes out
 * right by construction, but a base that is slightly off would only show up
 * when applied to a different percentage. Offering other provinces would be
 * false precision, so `berekenMrb` refuses them until we have real charges
 * from that province to calibrate against.
 */
export const GEKALIBREERDE_PROVINCIES = ['Utrecht'];
export const DEFAULT_MRB_PROVINCIE = 'Utrecht';
/**
 * Since 1 July 2026 the MRB is computed on `massa rijklaar`, which the RDW
 * reports and which runs 100 kg above the empty mass the tariff brackets were
 * originally written against.
 */
const RIJKLAAR_OFFSET = 100;
/** Rijksdeel per quarter, Wet MRB 1994 art. 23 lid 1. */
const RIJKSDEEL_BASIS = 64.24;
const RIJKSDEEL_PER_100KG = 17.27;
/** Opcenten base per quarter — calibrated, see the note above. */
const OPCENTEN_BASIS = 65.5;
const OPCENTEN_PER_100KG = 11.55;
/**
 * Percentage of the tariff an all-electric or hydrogen car pays (art. 23b).
 * 70% through 2028, 75% in 2029, and the provision lapses in 2030.
 */
export function elektrischPercentage(jaar) {
    if (jaar <= 2028)
        return 0.7;
    if (jaar === 2029)
        return 0.75;
    return 1;
}
/** Whether art. 23b applies: driven exclusively by electricity or hydrogen. */
export function isVolledigElektrisch(brandstof) {
    const b = (brandstof ?? '').toLowerCase();
    if (!b)
        return false;
    if (/benzine|diesel|lpg|gas|hybride/.test(b))
        return false;
    return /elektr|waterstof/.test(b);
}
/**
 * MRB for one car, or null when the mass is unknown (nothing to compute from)
 * or the province is one we cannot price yet.
 */
export function berekenMrb(input) {
    const { massaRijklaar, brandstof, provincie, jaar } = input;
    if (!massaRijklaar || massaRijklaar <= 0)
        return null;
    if (!GEKALIBREERDE_PROVINCIES.includes(provincie))
        return null;
    const opcentenPercentage = OPCENTEN_2026[provincie];
    if (opcentenPercentage == null)
        return null;
    const klasse = Math.round(massaRijklaar / 100) * 100;
    const stappen = Math.max(0, (klasse - 1000 - RIJKLAAR_OFFSET) / 100);
    const rijksdeelVol = RIJKSDEEL_BASIS + RIJKSDEEL_PER_100KG * stappen;
    const grondslag = OPCENTEN_BASIS + OPCENTEN_PER_100KG * stappen;
    const opcentenVol = (opcentenPercentage / 100) * grondslag;
    const factor = isVolledigElektrisch(brandstof) ? elektrischPercentage(jaar) : 1;
    const rijksdeel = rijksdeelVol * factor;
    const opcenten = opcentenVol * factor;
    const perKwartaal = rijksdeel + opcenten;
    return {
        klasse,
        rijksdeel: round2(rijksdeel),
        opcenten: round2(opcenten),
        perKwartaal: round2(perKwartaal),
        perMaand: round2(perKwartaal / 3),
        perJaar: round2(perKwartaal * 4),
        elektrischFactor: factor,
        opcentenPercentage,
    };
}
function round2(n) {
    return Math.round(n * 100) / 100;
}
//# sourceMappingURL=mrb.js.map